import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import EditIcon from '@mui/icons-material/Edit';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import TextsmsIcon from '@mui/icons-material/Textsms';
import { Box, styled } from '@mui/material';
import { isEmpty } from 'lodash-es';
import { ReactEventHandler } from 'react';
import { HiCursorClick, HiOfficeBuilding, HiOutlineArchive } from 'react-icons/hi';
import {
  ICandidate,
  ICandidateRevenue,
  IJob,
  ILocation,
  INotes,
  ISubmissionSettingsResponse,
  IUserDTO,
  Interaction,
  amplitude,
  candiateDefaultFieldMapping,
  fetchCandiateFieldMapping,
  getSubmissionSettings,
} from 'wisestep-api';
import {
  WsIconLocation,
  WsIconTime,
  WsIconPosition,
  WsIconLikeLocation,
  WsIconProfile,
  WsIconDice,
  WsColors,
  IWorkspaceContext,
  formatDateToMonthDayYearString,
  WsIconBoard,
  WsIconCurrency,
  WsIconVisa,
  timeSince,
  getDateUsingFormat,
  formatLocation,
  WsChipVariants,
} from 'wisestep-base';

import { getUserAnalyticsUrl } from '../../../features/candidates/utils/CandidateHelper';
import {
  getRawPhoneNumber,
  getSubmissionNoticePeriodInDays,
} from '../../../features/conversationWidget/utils/fieldDefaultValues';
import { failed, success, warning } from '../../../utils/NotificationMessages';
import { openUrlInNewTab } from '../../../utils/openUrlInNewTab';
import { CandidateDetails } from '../components/childComponets/CandidateDetailsCard/CandidateDetailsCard';
import {
  NoteActivityCardProps,
  NoteTagType,
} from '../components/childComponets/noteActivity/NoteActivity';

export const apiFailedMessage =
  'There was an error in processing your search request. We have logged the error and our team will review it.Please try again with different search criteria';
export const LayoutTheme = styled(Box)(() => ({
  maxWidth: '100%',
  maxHeight: 'xs',
  backgroundColor: WsColors.TertiaryShades50,
}));

export const AppLayoutTheme = styled(Box)(() => ({
  borderRadius: 8,
  backgroundColor: WsColors.TheBlues900,
}));

export enum CandidateType {
  SUBMISSIONS = 'submissions',
  TALENTPOOL = 'talentpool',
  PIPELINE = 'pipeline',
  SMART_SOURCE = 'smartSource',
}

export const copySuccess = () => success('Copied Successfully !!');
export const copyFailed = () => failed('Copy failed !! ');
export const copyWarn = () => warning('Clipboard not supported');

export const getCandidateDetails = (
  candidate: ICandidate,
  type: CandidateType
): CandidateDetails[] => {
  if (
    (candidate.source === 'LOCAL' ||
      candidate.source === 'CVUPLOAD_LOCAL' ||
      candidate.source === 'GDRIVE' ||
      candidate.source === 'CVUPLOAD_GDRIVE' ||
      candidate?.candidateStatusTags?.includes('applied')) &&
    (type === CandidateType.TALENTPOOL || type === CandidateType.PIPELINE)
  ) {
    return getParsedCandidateDetails(candidate, type);
  }
  const array: CandidateDetails[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate.currentLocation) ?? 'NA',
      title: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      value: `NA`,
      title: 'Candidate Location',
    });
  }
  if (
    !isEmpty(candidate.companyName) &&
    (type === CandidateType.SUBMISSIONS || type === CandidateType.PIPELINE)
  ) {
    array.push({
      icon: <HiOfficeBuilding color={WsColors.TertiaryShades700} />,
      value: candidate.companyName ?? 'NA',
      title: 'Client name',
    });
  }
  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      title: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.previousJobTitles?.[0],
      title: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: 'NA',
      title: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience || candidate?.totalMonthsExperience) {
    array.push({
      icon: <WsIconPosition />,
      value:
        candidate?.totalYearsExperience && candidate.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } years Exp`
          : `${candidate.totalYearsExperience ?? 0}.${
              candidate?.totalMonthsExperience ? candidate?.totalMonthsExperience % 12 : 0
            } year Exp`,
      title: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconPosition />,
      value: 'NA',
      title: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    value:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `NA`
        : candidate.source ?? `NA`,
    title: 'Source',
  });

  if (candidate.jobPreferencePermanent || candidate.jobPreferenceContract) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `Prefers ${candidate.jobPreferenceContract ? 'Contract' : 'Permanent'} `,
      title: 'Preferred Job Type',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Preferred Job Type',
    });
  }

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    value: getPreferredLocation(candidate),
    title: 'Preferred Locations',
  });

  array.push({
    icon: <WsIconLikeLocation color={WsColors.TertiaryShades700} />,
    value: candidate?.relocationPreference ?? 'NA',
    title: 'Relocation Preferences',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    value:
      candidate.expectedSalaryCurrency &&
      candidate.expectedBaseSalaryTimeSpan &&
      candidate.expectedBaseSalary
        ? `${candidate.expectedSalaryCurrency} ${String(candidate.expectedBaseSalary)} ${
            candidate.expectedBaseSalaryTimeSpan ?? ''
          }`
        : 'NA',
    title: 'Expected Salary',
  });

  array.push({
    icon: <WsIconCurrency color={WsColors.TertiaryShades700} />,
    value:
      candidate.currentSalaryCurrency && candidate.currentSalaryTimeSpan && candidate.currentSalary
        ? `${candidate.currentSalaryCurrency} ${String(candidate.currentSalary)} 
        ${candidate.currentSalaryTimeSpan}`
        : 'NA',
    title: 'Current Salary',
  });

  if (candidate?.appliedOn) {
    array.push({
      icon: <HiCursorClick color={WsColors.TertiaryShades700} />,
      value: (candidate?.appliedOn && timeSince(new Date(candidate?.appliedOn).toString())) ?? 'NA',
      title: 'Applied On',
    });
  }

  array.push({
    icon: <WsIconVisa />,
    value: `${
      candidate.workAuthorization && candidate.workAuthorization?.length > 0
        ? candidate.workAuthorization
        : candidate.workAuthorizationCategory && candidate.workAuthorizationCategory.length > 0
        ? candidate.workAuthorizationCategory
        : 'NA'
    }`,
    title: 'Visa Details',
  });

  if (candidate.noticePeriodInDays) {
    array.push({
      icon: <WsIconBoard />,
      value: `${candidate.noticePeriodInDays} Days`,
      title: 'Notice period',
    });
  } else {
    array.push({
      icon: <WsIconBoard />,
      value: `NA`,
      title: 'Notice period',
    });
  }
  if (!isEmpty(candidate.jobClientRecruiterName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.jobClientRecruiterName ?? 'NA',
      title: 'Client SPOC',
    });
  }
  if (!isEmpty(candidate.jobAccountManagerName) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.jobAccountManagerName ?? `NA`,
      title: 'Account manager',
    });
  }
  if (!isEmpty(candidate.jobLocations) && type === CandidateType.SUBMISSIONS) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate?.jobLocations?.[0] as ILocation) ?? `NA`,
      title: 'Job Location',
    });
  }
  return array;
};

const getParsedCandidateDetails = (
  candidate: ICandidate,
  type: CandidateType
): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  if (candidate.currentLocation) {
    array.push({
      icon: <WsIconLocation />,
      value: getLocation(candidate.currentLocation) ?? 'NA',
      title: 'Candidate Location',
    });
  } else {
    array.push({
      icon: <WsIconLocation />,
      value: `NA`,
      title: 'Candidate Location',
    });
  }

  if (candidate.currentJobTitle) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.currentEmployer
        ? `${candidate.currentJobTitle} @ ${candidate.currentEmployer}`
        : candidate.currentJobTitle,
      title: 'Latest JobTitle',
    });
  } else if (candidate.previousJobTitles?.[0]) {
    array.push({
      icon: <WsIconTime />,
      value: candidate.previousJobTitles?.[0],
      title: 'Latest JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: 'NA',
      title: 'Latest JobTitle',
    });
  }
  if (candidate?.totalYearsExperience) {
    array.push({
      icon: <WsIconTime />,
      value:
        candidate?.totalYearsExperience > 1
          ? `${candidate.totalYearsExperience} years Exp`
          : `${candidate.totalYearsExperience} year Exp`,
      title: "Candidate's Experience",
    });
  } else {
    array.push({
      icon: <WsIconTime />,
      value: `NA`,
      title: "Candidate's Experience",
    });
  }
  array.push({
    icon: <WsIconDice />,
    value:
      type === CandidateType.TALENTPOOL
        ? candidate.sources?.[0] ?? candidate.source ?? `NA`
        : candidate.source ?? `NA`,
    title: 'Source',
  });

  if (candidate.educationQualifications && candidate.educationQualifications?.length > 0) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `${candidate?.educationQualifications[0]} `,
      title: 'Education Qualifications',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Education Qualifications',
    });
  }

  if (candidate.education) {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `${candidate?.education[0]?.org} `,
      title: 'Education Inst',
    });
  } else {
    array.push({
      icon: <HiOutlineArchive size={16} color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Education Inst',
    });
  }

  if (candidate.previousJobTitles && candidate.previousJobTitles?.length > 0) {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      value: candidate.previousJobTitles[0],
      title: 'Previous JobTitle',
    });
  } else {
    array.push({
      icon: <WsIconPosition color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Previous JobTitle',
    });
  }
  if (candidate.currentEmployer) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.currentEmployer,
      title: 'Current Employer',
    });
  } else {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: `NA`,
      title: 'Current Employer',
    });
  }

  if (candidate.gender === 'FEMALE' || candidate.gender === 'MALE') {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.gender,
      title: 'Gender',
    });
  }
  if (candidate.dob) {
    array.push({
      icon: <WsIconTime color={WsColors.TertiaryShades700} />,
      value: formatDateToMonthDayYearString(new Date(candidate?.dob)),
      title: 'DOB',
    });
  }
  if (candidate.candidateLanguages && candidate?.candidateLanguages?.length > 0) {
    array.push({
      icon: <WsIconProfile color={WsColors.TertiaryShades700} />,
      value: candidate.candidateLanguages?.map((language) => language.languageName).toString(),
      title: 'Languages Know',
    });
  }

  return array;
};

export const getPersonalDetails = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.visaStatus) && candidate.visaStatus ? candidate.visaStatus : 'NA',
    title: 'Visa Status',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.visaValidity) && candidate.visaValidity ? candidate.visaValidity : 'NA',
    title: 'Visa Validity',
  });
  array.push({
    icon: <WsIconVisa />,
    value: `${
      candidate.workAuthorization && candidate.workAuthorization?.length > 0
        ? candidate.workAuthorization
        : candidate.workAuthorizationCategory && candidate.workAuthorizationCategory.length > 0
        ? candidate.workAuthorizationCategory
        : 'NA'
    }`,
    title: 'Authorization',
  });
  candidate?.alternateEmails?.length > 0 &&
    array.push({
      icon: <WsIconVisa />,
      value: candidate?.alternateEmails?.length > 0 ? candidate.alternateEmails : 'NA',
      title: 'Alternate Email Address',
      type: 'EMAIL',
    });
  candidate?.alternatePhoneNumbers?.length > 0 &&
    array.push({
      icon: <WsIconVisa />,
      value: candidate?.alternatePhoneNumbers?.length > 0 ? candidate.alternatePhoneNumbers : 'NA',
      title: 'Alternate Mobile Numbers',
      type: 'EMAIL',
    });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.socialProfiles ? candidate.socialProfiles.url : 'NA',
    title: 'Social Link',
    type: 'ICON',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.profileURLs && candidate.profileURLs.length > 0 ? candidate.profileURLs : 'NA',
  //   title: 'Profile Urls',
  // });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.nationality) ? (candidate.nationality as string) : 'NA',
    title: 'Nationality',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.dob ? new Date(candidate.dob).toLocaleDateString() : 'NA',
    title: 'Date of Birth',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.currentLocation && !isEmpty(getLocation(candidate?.currentLocation))
        ? getLocation(candidate?.currentLocation)
        : 'NA',
    title: 'Current Location',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.gender) && candidate.gender ? candidate.gender : 'NA',
    title: 'Gender',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: 'NA',
  //   title: 'Messenger ID',
  // });
  return array;
};

export const getProfessionalDetails = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.currentEmployer) && candidate.currentEmployer
        ? candidate.currentEmployer
        : 'NA',
    title: 'Latest Employer',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.currentJobTitle) && candidate.currentJobTitle
        ? candidate.currentJobTitle
        : 'NA',
    title: 'Latest Job Title',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.totalYearsExperience ? `${candidate.totalYearsExperience}` : 'NA',
    title: 'Total experience',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.previousEmployers) && candidate.previousEmployers
        ? candidate.previousEmployers
        : 'NA',
    title: 'Previous Employers',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.previousJobTitles) && candidate.previousJobTitles
        ? candidate.previousJobTitles
        : 'NA',
    title: 'Previous Job Titles',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.relevantYearsExperience ? `${candidate.relevantYearsExperience}` : 'NA',
    title: 'Relevant experience',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value:
  //     !isEmpty(candidate.certifications) && candidate.certifications
  //       ? candidate.certifications
  //       : 'NA',
  //   title: 'Certifications',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate?.educationQualifications && candidate?.educationQualifications?.length > 0
        ? candidate?.educationQualifications
        : candidate?.highestEducation
        ? candidate?.highestEducation
        : 'NA',
    title: 'Highest education qualifications',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.institutionsAttended) && candidate.institutionsAttended
        ? candidate.institutionsAttended
        : 'NA',
    title: 'Institutions Attended',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: 'NA',
  //   title: 'Documents',
  // });
  return array;
};
export const getCompenstation = (
  candidate: ICandidate,
  revenue?: ICandidateRevenue | undefined
): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  const isSubmitted = candidate?.candidateStatusTags?.includes('submitted');
  const currentComp = {
    icon: <WsIconVisa />,
    value: candidate?.currentSalary
      ? `${candidate.currentSalaryCurrency ?? ''} ${candidate.currentSalary} ${
          candidate.currentSalaryTimeSpan ?? ''
        }`
      : 'NA',
    title: 'Current Salary',
  };

  const expectedComp = {
    icon: <WsIconVisa />,
    value: candidate?.expectedBaseSalary
      ? `${candidate.expectedSalaryCurrency ?? ''} ${candidate.expectedBaseSalary} ${
          candidate.expectedBaseSalaryTimeSpan ?? ''
        }`
      : 'NA',
    title: 'Expected Salary',
  };

  const clientBillRateFromCandidate: CandidateDetails = {
    icon: <WsIconVisa />,
    value: candidate?.clientBillRate
      ? `${candidate.clientBillRateCurrency ?? ''} ${candidate.clientBillRate} ${
          candidate.clientBillRateTimeSpan ?? ''
        }`
      : 'NA',
    title: 'Client Bill Rate',
  };

  if (isSubmitted) {
    array.push(clientBillRateFromCandidate, expectedComp, currentComp);
  } else {
    array.push(currentComp, expectedComp);
  }

  const revenueDetails: CandidateDetails = {
    icon: <WsIconVisa />,
    value: revenue
      ? `${revenue.clientBillRateCurrency} ${revenue.netFeeIncome} ${revenue.clientBillRateTimeSpan}`
      : 'NA',
    title: 'Client Bill Rate',
  };

  const potentialNetMargin: CandidateDetails = {
    icon: <WsIconVisa />,
    value: revenue
      ? `${revenue.clientBillRateCurrency} ${revenue.netMargin?.toString()} ${
          revenue.clientBillRateTimeSpan
        }`
      : 'NA',
    title: 'Potential Net Margin',
  };

  if (candidate.jobType === 'Permanent') array.push(revenueDetails);
  else array.push(potentialNetMargin);

  const expectedAllowancesAndBenefits: CandidateDetails = {
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.otherExpectedAllowances) && candidate.otherExpectedAllowances
        ? candidate.otherExpectedAllowances
        : 'NA',
    title: 'Expected allowances and benefits',
  };
  const otherAllowancesAndBenefits: CandidateDetails = {
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.otherCurrentAllowances) && candidate.otherCurrentAllowances
        ? candidate.otherCurrentAllowances
        : 'NA',
    title: 'Other allowances and benefits',
  };
  array.push(expectedAllowancesAndBenefits, otherAllowancesAndBenefits);
  return array;
};

export const getJobPreferencInfo = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.jobPreferencePermanent && candidate.jobPreferenceContract
        ? 'Contract & Permanent'
        : candidate.jobPreferenceContract
        ? 'Contract '
        : candidate.commPreferencePhone
        ? 'Permanent'
        : 'NA',
    title: 'Job Preference',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.currentJobType ?? 'NA',
    title: 'Latest Job Type',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.willingToRelocate != null ? (candidate.willingToRelocate ? 'YES' : 'NO') : 'NA',
    title: 'Willing to Relocate',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.willingToTravel != null ? (candidate.willingToTravel ? 'YES' : 'NO') : 'NA',
  //   title: 'Willing to Travel',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.jobIncludesTravel != null ? (candidate.jobIncludesTravel ? 'YES' : 'NO') : 'NA',
    title: 'Latest Job Includes Travel',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.familyWillingToRelocate != null
        ? candidate.familyWillingToRelocate
          ? 'YES'
          : 'NO'
        : 'NA',
    title: 'Family willing to relocate',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate.noticePeriod ?? 'NA',
    title: 'Notice Period',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.interviewingWithOthers != null
        ? candidate.interviewingWithOthers
          ? 'YES'
          : 'NO'
        : 'NA',
    title: 'Interviewing With Others',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.currentLocation ? getLocation(candidate.currentLocation) : 'NA',
  //   title: 'Location',
  // });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.availableFrom ?? 'NA',
  //   title: 'Available From',
  // });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.reasonForNewJob) && candidate.reasonForNewJob
        ? candidate.reasonForNewJob
        : 'NA',
    title: 'Reason for looking for a new job',
  });
  return array;
};
export const getOtherInfo = (candidate: ICandidate): CandidateDetails[] => {
  const array: CandidateDetails[] = [];
  array.push({
    icon: <WsIconVisa />,
    value:
      candidate.commPreferenceEmail && candidate.commPreferencePhone
        ? 'Email & Phone'
        : candidate.commPreferenceEmail
        ? 'Email '
        : candidate.commPreferencePhone
        ? 'Phone'
        : 'NA',
    title: 'Communication Preference',
  });
  array.push({
    icon: <WsIconVisa />,
    value: candidate?.sources?.[0] ?? 'NA',
    title: 'Source',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.candidateLanguages) && candidate.candidateLanguages
        ? candidate.candidateLanguages.map((language) => language.languageName)
        : 'NA',
    title: 'Languages Known',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.tags) && candidate.tags ? candidate.tags : 'NA',
    title: 'Tags',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.aadharNumber) && candidate.aadharNumber ? candidate.aadharNumber : 'NA',
    title: 'Aadhar Number',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.panCard) && candidate.panCard ? candidate.panCard : 'NA',
    title: 'PAN Card',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.passportNumber) && candidate.passportNumber
        ? candidate.passportNumber
        : 'NA',
    title: 'Passport Number',
  });
  array.push({
    icon: <WsIconVisa />,
    value:
      !isEmpty(candidate.passportType) && candidate.passportType ? candidate.passportType : 'NA',
    title: 'Passport Type',
  });
  array.push({
    icon: <WsIconVisa />,
    value: !isEmpty(candidate.ssnNumber) && candidate.ssnNumber ? candidate.ssnNumber : 'NA',
    title: 'SSN Number',
  });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: candidate.candidateId ?? 'NA',
  //   title: 'Candidate ID',
  // });
  // array.push({
  //   icon: <WsIconVisa />,
  //   value: !isEmpty(candidate.assessment) && candidate.assessment ? candidate.assessment : 'NA',
  //   title: 'Recruiter Assessment',
  // });
  return array;
};

export interface CandidateCardProps {
  candidate: ICandidate;
  downloadSource?: string | null;
  job?: IJob | null;
  isSelected: boolean;
  checkBoxOnclick?: ReactEventHandler;
  candidateIndex?: number;
  onCVClick?: (args: number) => void;
  setDownloadSource?: (args: string, index: number) => void;
  onActionClick?: (args: number) => void;
  openSingleEmail: (candidate: ICandidate, email: string) => void;
  queryRefresh?: () => void;
  interaction?: Interaction | undefined;
  amplitudeEventHandler?: (eventType: string) => void;
  onOpenShareIcon?: (args: ICandidate) => void;
  isCvViewed?: boolean;
}

export const getLocation = (location: ILocation) =>
  [location.city, location.state, location.country].filter((x) => x).toString();
export const phoneOptions = [
  {
    label: 'Send SMS',
    onClick: () => {},
    icon: <TextsmsIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Edit',
    onClick: () => {},
    icon: <EditIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Call',
    onClick: () => {},
    icon: (
      <LocalPhoneOutlinedIcon key={0} fontSize="small" sx={{ color: WsColors.TertiaryShades500 }} />
    ),
  },
];

export const emailOptions = [
  {
    label: 'Send Email',
    id: '0',
    onClick: () => {},
    icon: <MailOutlineIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Copy',
    id: '1',
    onClick: () => {},
    icon: <ContentCopyIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
  {
    label: 'Mark as Primary',
    id: '2',
    onClick: () => {},
    icon: <MailOutlineIcon key={0} fontSize="small" sx={{ color: WsColors.TheBlues500 }} />,
  },
];

export const firingAmplitudeEvents = (
  name: string,
  clickedOnName: string,
  workspace: IWorkspaceContext,
  pageName: string
) =>
  amplitude(name, {
    clickedOn: clickedOnName,
    workspaceName: workspace.name,
    pageName: pageName,
    planType: workspace.planType,
    interfaceType: 'web',
    pageUrl: window.location.href,
  });

export const getCountryByLocation = (locations: ILocation[]) => {
  const countryLocations: ILocation[] = [];
  locations?.length > 0 &&
    locations.forEach((location) => {
      const countryLocation = {
        country: location.country,
        countryCode: location.countryCode,
        city: '',
        continent: '',
        continentCode: '',
        state: '',
        stateCode: '',
        point: location.point,
      };
      countryLocations.push(countryLocation);
    });
  return countryLocations;
};

export const getAllAlternateNumbers = (data: string[]) => {
  if (!isEmpty(data) && data.includes('false')) {
    return data.filter((val) => val != 'false');
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicates = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return data.filter((val) => val != value);
  }
  return isEmpty(data) ? [] : data;
};

export const removeDuplicatesPhNos = (data: string[], value: string) => {
  if (!isEmpty(data)) {
    return getAllAlternateNumbers(data).filter(
      (val) => !getRawPhoneNumber(val).includes(getRawPhoneNumber(value))
    );
  }
  return isEmpty(data) ? [] : data;
};

const getPreferredLocation = (candidate: ICandidate): string => {
  let preferredLocation = '';
  `${
    candidate?.preferredLocations &&
    candidate?.preferredLocations?.map(
      (location) =>
        (preferredLocation =
          preferredLocation +
          `${location.city ?? ''}  ${location.state ?? ''} ${location.country ?? ''}, `)
    )
  }`;
  return preferredLocation ? preferredLocation : 'NA';
};

export const getFieldSettings = async (isMapping: boolean) => {
  if (isMapping) {
    const req = await fetchCandiateFieldMapping();
    if (!isEmpty(req.data)) {
      return req.data;
    } else {
      const resetDefaultSettingsDetails = await candiateDefaultFieldMapping();
      return resetDefaultSettingsDetails.data;
    }
  }
  const req = await getSubmissionSettings();
  return req.data;
};

export const getSubmissionNotesActivities = (submission: ICandidate): NoteActivityCardProps[] =>
  (submission?.candidateNotes ?? []).map((note: INotes) => {
    const tags: NoteTagType[] = [];

    if (note?.applicationStage) {
      tags.push({
        label: note?.applicationStage,
        variant: WsChipVariants.STATUS,
        borderColor: WsColors.YellowBorder,
      });
    }
    if (note?.applicationState) {
      tags.push({
        label: note.applicationState,
        variant: WsChipVariants.STAGE,
        borderColor: WsColors.TheBlues400,
      });
    }
    return {
      noteText: note?.notes ?? '',
      timestamp: note?.createdAt ?? '',
      userName: note?.createdBy ?? '',
      recruiterId: note?.recruiterId ?? '',
      tags,
    } as NoteActivityCardProps;
  });

export const getRecruiterFullName = (recruiter: IUserDTO): string => {
  const firstName = recruiter?.firstName?.trim();
  const lastName = recruiter?.lastName?.trim();

  if (firstName && lastName) return `${firstName} ${lastName}`;
  if (firstName) return firstName;
  if (lastName) return lastName;
  return recruiter?.email ?? '';
};

export const enum SUBMISSION_TYPE {
  TEXT,
  LINK,
  CHIP,
}

export interface SubmissionDetail {
  label: string;
  value: string | string[];
  component: SUBMISSION_TYPE;
}

export const getSubmissionDetails = (
  label: string,
  value: string | string[],
  component: SUBMISSION_TYPE
): SubmissionDetail => {
  return { label, value, component };
};

const getSubmissionFieldValue = (
  value: string | null | undefined | string[],
  fallback = '-'
): string | string[] => (!!value && !isEmpty(value) ? value : fallback);

export const submissionFieldConfigs = (
  submission: ICandidate
): {
  key: keyof ISubmissionSettingsResponse;
  label: string;
  component: SUBMISSION_TYPE;
  valueKey: keyof ICandidate;
  value: string | string[];
}[] => [
  {
    key: 'name',
    label: 'Name',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'name',
    value: submission?.name ?? '-',
  },
  {
    key: 'email',
    label: 'Email',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'email',
    value: submission?.email ?? '-',
  },
  // { key: 'uploadCV', label: 'Upload CV', component: SUBMISSION_TYPE.TEXT },
  // {
  //   key: 'messengerId',
  //   label: 'Messenger ID',
  //   component: SUBMISSION_TYPE.TEXT,
  //   valueKey: 'messengers',
  // },
  {
    key: 'visaType',
    label: 'Visa Type',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'visaType',
    value: submission?.visaType ?? '-',
  },
  {
    key: 'visaValidity',
    label: 'Visa Validity',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'visaValidity',
    value: submission?.visaValidity
      ? getDateUsingFormat(submission?.visaValidity, 'DD MMM, YYYY')
      : '-',
  },
  {
    key: 'nationality',
    label: 'Nationality',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'nationality',
    value: getSubmissionFieldValue(submission?.nationality),
  },
  {
    key: 'alternateEmail',
    label: 'Alternate Email',
    component: SUBMISSION_TYPE.CHIP,
    valueKey: 'alternateEmails',
    value: getSubmissionFieldValue(submission?.alternateEmails),
  },
  {
    key: 'alternatePhoneNumber',
    label: 'Alternate Phone Number',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'alternatePhoneNumbers',
    value: getSubmissionFieldValue(submission?.alternatePhoneNumbers),
  },
  {
    key: 'jobPreferences',
    label: 'Job Preferences',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'preferredJobTypes',
    value: submission?.preferredJobTypes?.join(',') ?? '-',
  },
  {
    key: 'jobType',
    label: 'Job Type',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'jobType',
    value: submission?.jobType ?? '-',
  },
  {
    key: 'phoneNumber',
    label: 'Phone Number',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'phoneNumber',
    value: submission?.phoneNumber ?? '-',
  },
  {
    key: 'linkedInURL',
    label: 'LinkedIn URL',
    component: SUBMISSION_TYPE.LINK,
    valueKey: 'linkedInURL',
    value: submission?.linkedInURL ? [submission?.linkedInURL, submission?.linkedInURL] : '-',
  },
  // { key: 'candidateID', label: 'Candidate ID', component: SUBMISSION_TYPE.TEXT },
  {
    key: 'currentLocation',
    label: 'Current Location',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'currentLocation',
    value: submission?.currentLocation ? formatLocation(submission?.currentLocation) : '-',
  },
  {
    key: 'workAuthorization',
    label: 'Work Authorization',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'workAuthValidity',
    value: submission?.workAuthorization ?? '-',
  },
  // { key: 'source', label: 'Source', component: SUBMISSION_TYPE.TEXT, valueKey: 'sources' },
  {
    key: 'skills',
    label: 'Skills',
    component: SUBMISSION_TYPE.CHIP,
    valueKey: 'skills',
    value: getSubmissionFieldValue(submission?.skills),
  },
  {
    key: 'totalExperience',
    label: 'Total Experience',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'totalYearsExperience',
    value: submission?.totalYearsExperience ? `${submission?.totalYearsExperience} years` : '-',
  },
  // { key: 'otherDocuments', label: 'Other Documents', component: SUBMISSION_TYPE.TEXT },
  {
    key: 'relevantExperience',
    label: 'Relevant Experience',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'relevantYearsExperience',
    value: submission?.relevantYearsExperience
      ? `${submission?.relevantYearsExperience} years`
      : '-',
  },
  {
    key: 'highestEducation',
    label: 'Highest Education',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'highestEducation',
    value: submission?.highestEducation ?? '-',
  },
  {
    key: 'institutesAttended',
    label: 'Institutes Attended',
    component: SUBMISSION_TYPE.CHIP,
    valueKey: 'institutionsAttended',
    value: getSubmissionFieldValue(submission?.institutionsAttended),
  },
  {
    key: 'currentJobTitle',
    label: 'Current Job Title',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'currentJobTitle',
    value: submission?.currentJobTitle ?? '-',
  },
  {
    key: 'previousJobTitles',
    label: 'Previous Job Titles',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'preferredJobTitles',
    value: submission?.preferredJobTitles?.join(', ') ?? '-',
  },
  {
    key: 'currentEmployer',
    label: 'Current Employer',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'currentEmployer',
    value: submission?.currentEmployer ?? '-',
  },
  {
    key: 'previousEmployers',
    label: 'Previous Employers',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'previousEmployers',
    value: submission?.previousEmployers?.join(', ') ?? '-',
  },
  {
    key: 'certifications',
    label: 'Certifications',
    component: SUBMISSION_TYPE.CHIP,
    valueKey: 'certifications',
    value: getSubmissionFieldValue(submission?.certifications),
  },
  {
    key: 'preferredLocation',
    label: 'Preferred Location',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'preferredLocations',
    value: submission?.preferredLocations?.length
      ? formatLocation(submission?.preferredLocations?.[0])
      : '-',
  },
  {
    key: 'relocationPreferences',
    label: 'Relocation Preferences',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'relocationPreference',
    value: submission?.relocationPreference ?? '-',
  },
  {
    key: 'currentCompensation',
    label: 'Current Compensation',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'currentSalary',
    value:
      submission?.currentSalary &&
      submission?.currentSalaryCurrency &&
      submission?.currentSalaryTimeSpan
        ? `${submission?.currentSalaryCurrency} ${submission?.currentSalary} ${submission?.currentSalaryTimeSpan}`
        : '-',
  },
  {
    key: 'expectedCompensation',
    label: 'Expected Compensation',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'expectedBaseSalary',
    value:
      submission?.expectedSalaryCurrency &&
      submission?.expectedBaseSalary &&
      submission?.expectedBaseSalaryTimeSpan
        ? `${submission?.expectedSalaryCurrency} ${submission?.expectedBaseSalary} ${submission?.expectedBaseSalaryTimeSpan}`
        : '-',
  },
  {
    key: 'communicationPreferences',
    label: 'Communication Preferences',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'commPreferenceEmail',
    value: submission?.commPreferenceEmail
      ? submission?.commPreferencePhone
        ? 'Email , Phone'
        : 'Email'
      : submission?.commPreferencePhone
      ? 'Phone '
      : '-',
  },
  {
    key: 'availableTimeSlot',
    label: 'Available Time Slot',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'availableFrom',
    value: submission?.availableFrom
      ? getDateUsingFormat(submission?.availableFrom, 'DD MMM, YYYY')
      : '-',
  },
  {
    key: 'noticePeriod',
    label: 'Notice Period',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'noticePeriodInDays',
    value: submission?.noticePeriodInDays
      ? getSubmissionNoticePeriodInDays(submission?.noticePeriodInDays)?.key ?? '-'
      : '-',
  },
  {
    key: 'reasonForChange',
    label: 'Reason For Change',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'reasonForNewJob',
    value: getSubmissionFieldValue(submission?.reasonForNewJob),
  },
  {
    key: 'interviewingAnywhereElse',
    label: 'Interviewing Anywhere Else',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'interviewingWithOthers',
    value: submission?.interviewingWithOthers ? 'Yes' : 'No',
  },
  {
    key: 'yourAssessment',
    label: 'Recuiter Assessment',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'assessment',
    value: getSubmissionFieldValue(submission?.assessment),
  },
  // { key: 'notes', label: 'Notes', component: SUBMISSION_TYPE.CHIP },
  // { key: 'questions', label: 'Questions', component: SUBMISSION_TYPE.TEXT },
  {
    key: 'familyWillingToRelocate',
    label: 'Family Willing To Relocate',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'familyWillingToRelocate',
    value: submission?.familyWillingToRelocate ? 'Yes' : 'No',
  },
  {
    key: 'otherAllowance',
    label: 'Other Allowance',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'otherCurrentAllowances',
    value: getSubmissionFieldValue(submission?.otherCurrentAllowances),
  },
  // { key: 'otherInterviewStage', label: 'Other Interview Stage', component: SUBMISSION_TYPE.TEXT },
  {
    key: 'workAuthorizationCategory',
    label: 'Work Authorization Category',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'workAuthorizationCategory',
    value: getSubmissionFieldValue(submission?.workAuthorizationCategory),
  },
  {
    key: 'currentJobIncludeTravel',
    label: 'Current Job Includes Travel',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'currentJobTravelYes',
    value: submission?.currentJobTravelYes ? 'Yes' : 'No',
  },
  {
    key: 'willingToTravel',
    label: 'Willing To Travel',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'willingToTravel',
    value: submission?.willingToTravel ? 'Yes' : 'No',
  },
  {
    key: 'willingToRelocate',
    label: 'Willing To Relocate',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'willingToRelocate',
    value: submission?.willingToRelocate ? 'Yes' : 'No',
  },
  {
    key: 'expectedAllowance',
    label: 'Expected Allowance',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'otherExpectedAllowances',
    value: submission?.otherExpectedAllowances ?? '-',
  },
  // { key: 'candidatePersonalId', label: 'Candidate Personal ID', component: SUBMISSION_TYPE.TEXT },
  {
    key: 'skillMatrix',
    label: 'Skill Matrix',
    component: SUBMISSION_TYPE.CHIP,
    valueKey: 'skillMatrixRatings',
    value:
      submission?.skillMatrixRatings?.map(
        (skillMatrix) => `${skillMatrix?.skill} - ${skillMatrix?.rating}`
      ) ?? '-',
  },
  {
    key: 'exEmployeeOfClient',
    label: 'Ex-Employee Of Client',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'exEmployeeOfClient',
    value: submission?.exEmployeeOfClient ? 'Yes' : 'No',
  },
  {
    key: 'exEmployeeId',
    label: 'Ex-Employee ID',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'exEmployeeId',
    value: getSubmissionFieldValue(submission?.exEmployeeId),
  },
  {
    key: 'portalId',
    label: 'Portal ID',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'portalId',
    value: getSubmissionFieldValue(submission?.portalId),
  },
  {
    key: 'workAuthValidity',
    label: 'Work Auth Validity',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'workAuthValidity',
    value: submission?.workAuthValidity
      ? getDateUsingFormat(submission?.workAuthValidity.toString(), 'DD MMM, YYYY')
      : '-',
  },
  // {
  //   key: 'rtrConsent',
  //   label: 'RTR Consent',
  //   component: SUBMISSION_TYPE.TEXT,
  //   valueKey: 'rtrConsent',
  // },
  {
    key: 'passportType',
    label: 'Passport Type',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'passportType',
    value: submission?.passportType ?? '-',
  },
  {
    key: 'passportNumber',
    label: 'Passport Number',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'passportNumber',
    value: submission?.passportNumber ?? '-',
  },
  {
    key: 'gender',
    label: 'Gender',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'gender',
    value: submission?.gender ?? '-',
  },
  {
    key: 'DOB',
    label: 'Date of Birth',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'dob',
    value: submission?.dob ? getDateUsingFormat(submission?.dob.toString(), 'DD MMM, YYYY') : '-',
  },
  {
    key: 'age',
    label: 'Age',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'age',
    value: submission?.age && !isEmpty(submission?.age) ? submission?.age?.toString() : '-',
  },
  {
    key: 'postalAddress',
    label: 'Postal Address',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'postalAddress',
    value: getSubmissionFieldValue(submission?.postalAddress),
  },
  {
    key: 'ssnNumber',
    label: 'SSN Number',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'ssnNumber',
    value: getSubmissionFieldValue(submission?.ssnNumber),
  },
  {
    key: 'panCard',
    label: 'PAN Card',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'panCard',
    value: getSubmissionFieldValue(submission?.panCard),
  },
  {
    key: 'aadharNumber',
    label: 'Aadhar Number',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'aadharNumber',
    value: getSubmissionFieldValue(submission?.aadharNumber),
  },
  {
    key: 'businessUnit',
    label: 'Business Unit',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'businessUnit',
    value: submission?.businessUnit ?? '-',
  },
  {
    key: 'clientBillRate',
    label: 'Client Bill Rate',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'clientBillRate',
    value:
      submission?.clientBillRate &&
      submission?.clientBillRateCurrency &&
      submission.clientBillRateTimeSpan
        ? `${submission?.clientBillRateCurrency} ${submission?.clientBillRate} ${submission.clientBillRateTimeSpan}`
        : '-',
  },
  {
    key: 'offeredPayRate',
    label: 'Offered Pay Rate',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'offeredPayRate',
    value:
      submission?.offeredPayRate &&
      submission?.offeredPayRateCurrency &&
      submission.offeredPayRateTimeSpan
        ? `${submission?.offeredPayRateCurrency} ${submission?.offeredPayRate} ${submission.offeredPayRateTimeSpan}`
        : '-',
  },
  {
    key: 'payRateExpenses',
    label: 'Pay Rate Expenses',
    component: SUBMISSION_TYPE.TEXT,
    valueKey: 'payRateExpenses',
    value:
      submission?.payRateExpenses &&
      submission?.payRateExpensesCurrency &&
      submission.payRateExpensesType
        ? `${submission?.payRateExpensesCurrency} ${submission?.payRateExpenses} ${submission.payRateExpensesType}`
        : '-',
  },
];

export const getSubmissionDetailsWithComponent = (
  submission: ICandidate,
  submissionSetting: ISubmissionSettingsResponse
): SubmissionDetail[] => {
  return submissionFieldConfigs(submission)
    .filter(({ key }) => !submissionSetting?.[key]?.hidden)
    .map(({ label, component, value }) => getSubmissionDetails(label, value ?? '-', component));
};

export const isValidDash = (value: string | string[]): boolean => {
  if (Array.isArray(value)) {
    return value[0] === '-';
  }
  return value === '-';
};

export const onRecruiterNameClick = async (recruiterId: string) => {
  const url = await getUserAnalyticsUrl(recruiterId ?? '');
  openUrlInNewTab(url);
};

export const getCandidateLinkedInUrl = (linkedinUrl: string, profileUrls: string[]): string => {
  if (!isEmpty(linkedinUrl.trim())) {
    return linkedinUrl.includes('linkedin.com') ? linkedinUrl.trim() : '';
  }
  if (!isEmpty(profileUrls)) {
    return profileUrls.find((url) => url.includes('linkedin.com')) ?? '';
  }
  return '';
};
